import React from "react";
import { Modal, Box, Typography, Tooltip, CircularProgress } from "@mui/joy";
import type { Training } from "../../api/training/Training.typed";
import type { Dive, FollowUpSheet } from "../../api/followup/Followup.typed";
import type { Comment } from "../../api/zekay/comments";
import { CloseRoundedIcon } from "../../styles/StyledMUI";
import IconButtonCTA from "../common/IconButtonCTA";
import { colorTokens } from "../../styles/globalStyles";

type RefreshFunction<T> = () => Promise<T>;
type RefreshFunctionWithId<T> = (id?: string) => Promise<T>;

type RefreshTrainingFunction =
  | RefreshFunction<Training[] | undefined>
  | RefreshFunctionWithId<Training[] | undefined>;

type FollowUpSummaryModalProps = {
  open: boolean;
  onClose: () => void;
  customerId: number | undefined;
  trainingTemplateId: number | undefined;
  setTrainings?: React.Dispatch<React.SetStateAction<Training[] | undefined>>;
  refreshTrainings?: RefreshTrainingFunction;
  followUpSheet?: FollowUpSheet;
  dives?: Dive[];
};

type CriteriaSummary = {
  description: string;
  status: string;
  comments: Comment[];
  isComplete: boolean;
};

type FollowUpFormWithModules = {
  modules?: Array<{ name: string; criteria: any[] }>;
};

async function generateModuleSummary(
  followUpSheet: FollowUpSheet,
): Promise<Record<string, CriteriaSummary[]>> {
  const moduleSummaries: Record<string, CriteriaSummary[]> = {};

  if (!followUpSheet?.Form) {
    return moduleSummaries;
  }

  const form = followUpSheet.Form as FollowUpFormWithModules;
  if (!form.modules) {
    return moduleSummaries;
  }

  form.modules.forEach((module: { name: string; criteria: any[] }) => {
    if (!moduleSummaries[module.name]) {
      moduleSummaries[module.name] = [];
    }

    module.criteria.forEach((criterion: any) => {
      const options = criterion.options || [];
      const responses = criterion.status || [];
      const missingOptions = options.filter((opt: string) => !responses.includes(opt));

      let status = "";

      if (options.includes("OK") && options.includes("A retravailler")) {
        status = `${responses.join(", ") || "Non rempli"}`;
      } else {
        const given = responses.length ? `Validé : ${responses.join(", ")}\n` : "";
        const missing = missingOptions.length ? `Manquant : ${missingOptions.join(", ")}` : "";

        status = `${given}${missing}`;
      }

      let isComplete = false;
      if (options.includes("OK") && options.includes("A retravailler")) {
        isComplete = responses.includes("OK");
      } else {
        isComplete = missingOptions.length === 0;
      }

      const commentsArray = criterion.comments_array || [];

      moduleSummaries[module.name].push({
        description: criterion.description,
        status,
        comments: commentsArray,
        isComplete,
      });
    });
  });

  return moduleSummaries;
}

const getColorForCriterion = (criterion: CriteriaSummary): string => {
  if (criterion.status.includes("OK")) {
    criterion.isComplete = true;
  }

  if (criterion.isComplete) {
    return "green";
  }

  if (criterion.status.includes("Manquant") && criterion.status.includes("Validé")) {
    return "orange";
  }

  if (criterion.status.includes("A retravailler")) {
    return "orange";
  }

  return "white";
};

export default function FollowUpSummaryModal(props: FollowUpSummaryModalProps) {
  const [dives, setDives] = React.useState<Dive[]>([]);
  const [moduleSummaries, setModuleSummaries] = React.useState<Record<string, CriteriaSummary[]>>(
    {},
  );
  const isSmallScreen = window.innerWidth <= 600;
  const [isLoading, setIsLoading] = React.useState(true);

  const headerStyles = {
    container: {
      position: "fixed",
      top: 10,
      left: 10,
      right: 10,
      height: "calc(100% - 30px)",
      overflow: "auto",
      borderRadius: "10px",
      padding: "clamp(0.5rem, 1.5vw, 1rem)",
      backgroundColor: "white",
      margin: "0 auto",
    },
    headerTypography: {
      color: "black",
      fontWeight: "bold",
      fontSize: "clamp(1rem, 3vw, 1.5rem)",
    },
    gridContainer: {
      display: "grid",
      gridTemplateColumns: "5% 17% 15% 8% 7% 9% 20%",
      gap: 1,
      padding: 1,
    },
    gridTypographyTitle: {
      color: "black",
      fontWeight: "bold",
      fontSize: "clamp(0.7rem, 2vw, 1rem)",
    },
    gridTypography: {
      color: "black",
      fontSize: "clamp(0.7rem, 2vw, 1rem)",
    },
  };

  const modulesStyles = {
    moduleContainer: {
      marginBottom: 4,
    },
    moduleHeader: {
      marginBottom: 1,
      marginTop: 2,
      color: "black",
      fontWeight: "bold",
    },
    gridContainer: {
      display: "grid",
      gridTemplateColumns: "2% 20% 20% 30%",
      gap: 1,
      padding: 1,
    },
    gridTypographyTitle: {
      color: "black",
      fontWeight: "bold",
      fontSize: "clamp(0.7rem, 2vw, 1rem)",
    },
    gridTypography: {
      color: "black",
      fontSize: "clamp(0.7rem, 2vw, 1rem)",
    },
  };

  React.useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      try {
        if (props.followUpSheet) {
          if (props.dives && props.dives.length > 0) {
            setDives(props.dives);
          }

          const summaries = await generateModuleSummary(props.followUpSheet);
          setModuleSummaries(summaries);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [props.followUpSheet, props.dives, props.customerId, props.trainingTemplateId]);

  if (isLoading) {
    return (
      <Modal open={props.open} onClose={props.onClose}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <>
        <Box
          sx={{
            position: "fixed",
            top: 10,
            left: 10,
            right: 10,
            height: "calc(100% - 30px)",
            overflow: "auto",
            borderRadius: "10px",
            padding: "clamp(0.5rem, 2vw, 1rem)",
            backgroundColor: "white",
            margin: "0 auto",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Typography sx={headerStyles.headerTypography} mb={2}>
              Fiche de suivi
            </Typography>
            <Box sx={headerStyles.gridContainer}>
              <Typography sx={headerStyles.gridTypographyTitle}>N°</Typography>
              <Typography sx={headerStyles.gridTypographyTitle}>Date</Typography>
              <Typography sx={headerStyles.gridTypographyTitle}>Moniteur</Typography>
              <Typography sx={headerStyles.gridTypographyTitle}>Site</Typography>
              <Typography sx={headerStyles.gridTypographyTitle}>P(m)</Typography>
              <Typography sx={headerStyles.gridTypographyTitle}>T(min)</Typography>
              <Typography sx={headerStyles.gridTypographyTitle}>
                {isSmallScreen ? "Notes" : "Commentaires"}
              </Typography>
            </Box>

            {dives.map((dive, idx) => (
              <Box
                key={idx}
                sx={{
                  ...headerStyles.gridContainer,
                  borderBottom: "1px solid grey",
                }}
              >
                <Typography sx={headerStyles.gridTypography}>{dive.Number}</Typography>
                <Typography sx={headerStyles.gridTypography}>
                  {new Date(dive.Date).toLocaleDateString("fr-FR")}
                </Typography>
                <Typography sx={headerStyles.gridTypography}>{dive.Instructor}</Typography>
                <Typography sx={headerStyles.gridTypography}>{dive.DiveSite}</Typography>
                <Typography sx={headerStyles.gridTypography}>{dive.DiveDepth}</Typography>
                <Typography sx={headerStyles.gridTypography}>{dive.DiveTime}</Typography>
                <Typography sx={headerStyles.gridTypography}>{dive.Comment}</Typography>
              </Box>
            ))}
          </Box>

          {Object.keys(moduleSummaries).map((moduleName, index) => (
            <Box key={index}>
              <Typography sx={modulesStyles.moduleHeader}>{moduleName}</Typography>
              <Box sx={modulesStyles.gridContainer}>
                <Typography sx={modulesStyles.gridTypographyTitle}></Typography>
                <Typography sx={modulesStyles.gridTypographyTitle}>Description</Typography>
                <Typography sx={modulesStyles.gridTypographyTitle}>Critères</Typography>
                <Typography sx={modulesStyles.gridTypographyTitle}>Commentaires</Typography>
              </Box>

              {moduleSummaries[moduleName].map((criterion, idx) => (
                <Box
                  key={idx}
                  sx={{
                    ...modulesStyles.gridContainer,
                    borderBottom: "1px solid grey",
                  }}
                >
                  <Typography
                    sx={{
                      borderLeft: `5px solid ${getColorForCriterion(criterion)}`,
                    }}
                  ></Typography>
                  <Typography sx={modulesStyles.gridTypography}>{criterion.description}</Typography>
                  <Typography sx={modulesStyles.gridTypography}>
                    {criterion.status.split("\n").map((line, index) => {
                      if (line.includes("Validé :")) {
                        return (
                          <span key={index}>
                            <strong style={{ color: "green" }}>{line.split(":")[0]}:</strong>{" "}
                            {line.split(":")[1]}
                            <br />
                          </span>
                        );
                      } else if (line.includes("Manquant :")) {
                        return (
                          <span key={index}>
                            <strong style={{ color: "orange" }}>{line.split(":")[0]}:</strong>{" "}
                            {line.split(":")[1]}
                            <br />
                          </span>
                        );
                      } else {
                        return (
                          <span key={index}>
                            {line}
                            <br />
                          </span>
                        );
                      }
                    })}
                  </Typography>
                  <Typography sx={modulesStyles.gridTypography}>
                    {criterion.comments.length > 0 ? (
                      <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
                        {criterion.comments.map((comment, cIdx) => (
                          <li
                            key={cIdx}
                            style={{
                              marginBottom: "10px",
                              padding: "8px",
                              backgroundColor: colorTokens.bg.sheetLight,
                              borderRadius: "4px",
                            }}
                          >
                            <Box>
                              <Typography level="body-sm" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {comment.user} -{" "}
                                {new Date(comment.timestamp).toLocaleString("fr-FR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}{" "}
                                :
                              </Typography>
                              <Typography level="body-sm" sx={{ ml: 1 }}>
                                {comment.comment}
                              </Typography>
                            </Box>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      ""
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            right: 0,
            width: "150px",
            height: "60px",
            backgroundColor: colorTokens.bg.sheetLight,
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "10px 0 0 0",
          }}
        >
          <Tooltip title="Fermer">
            <IconButtonCTA
              aria-label="Fermer la fiche de suivi"
              ctaVariant="neutral"
              size="sm"
              onClick={props.onClose}
            >
              <CloseRoundedIcon />
            </IconButtonCTA>
          </Tooltip>
        </Box>
      </>
    </Modal>
  );
}
